// ManualLogoStep.tsx — step 'manual-logo' del onboarding (vía manual).
// Qué hace: subir un logo de archivo, crear uno con IA (botón "Create a logo with
//   AI" → setAiLogoOrigin('manual') → step 'ai-logo'), pegar una URL, o saltar.
// Qué exporta: ManualLogoStep.
// Quién lo usa: CreateWorkspaceModal.

import { RefObject } from 'react';
import { Button } from '../../Button';
import { WizardStep } from '../types';
import { HiddenInput, SourceChoiceGrid, SourceChoiceBtn, SourceIcon, SourceLabel, SourceDesc, UrlRow, UrlInput } from '../styled';

interface Props {
  fileRef: RefObject<HTMLInputElement>;
  logoFile: File | null;
  setLogoFile: (f: File | null) => void;
  logoPreview: string;
  setLogoPreview: (v: string) => void;
  logoUrlInput: string;
  setLogoUrlInput: (v: string) => void;
  setSelectedLogo: (v: string | null) => void;
  setAiLogoOrigin: (v: 'scan' | 'manual') => void;
  goToTemplates: () => void;
  user: (text: string) => void;
  novah: (text: string) => void;
  pushStep: (next: WizardStep) => void;
}

export function ManualLogoStep({
  fileRef, logoFile, setLogoFile, logoPreview, setLogoPreview, logoUrlInput,
  setLogoUrlInput, setSelectedLogo, setAiLogoOrigin, goToTemplates, user, novah, pushStep,
}: Props) {
  return (
    <>
      <HiddenInput ref={fileRef} type="file" accept="image/*" onChange={e => {
        const f = e.target.files?.[0];
        if (f) { setLogoFile(f); setLogoPreview(URL.createObjectURL(f)); setLogoUrlInput(''); }
      }} />

      {/* Preview */}
      {(logoPreview || logoUrlInput.trim()) && (
        <div style={{ width: 72, height: 72, margin: '0 auto 10px', border: '2px solid #E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', borderRadius: 12, background: '#f8fafc' }}>
          <img src={logoPreview || logoUrlInput.trim()} alt="Logo" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
        </div>
      )}

      {/* Options as source choice cards */}
      <SourceChoiceGrid>
        <SourceChoiceBtn onClick={() => fileRef.current?.click()}>
          <SourceIcon>📁</SourceIcon>
          <div>
            <SourceLabel>{logoFile ? logoFile.name : 'Upload from device'}</SourceLabel>
            <br /><SourceDesc>JPG, PNG, WebP, SVG</SourceDesc>
          </div>
        </SourceChoiceBtn>
        <SourceChoiceBtn onClick={() => {
          setAiLogoOrigin('manual');
          user("Create a logo with AI");
          novah("Pick a style and I'll design a few logos for you.");
          pushStep('ai-logo');
        }}>
          <SourceIcon>✨</SourceIcon>
          <div>
            <SourceLabel>Create a logo with AI</SourceLabel>
            <br /><SourceDesc>Don't have a logo? I'll design one for your brand</SourceDesc>
          </div>
        </SourceChoiceBtn>
      </SourceChoiceGrid>

      {/* URL input */}
      <div style={{ margin: '8px 0' }}>
        <UrlRow>
          <UrlInput
            value={logoUrlInput}
            onChange={e => { setLogoUrlInput(e.target.value); setSelectedLogo(e.target.value); setLogoFile(null); setLogoPreview(''); }}
            placeholder="Or paste a logo URL..."
          />
        </UrlRow>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 8 }}>
        <Button type="button" $variant="ghost" $size="sm" onClick={() => {
          user('Skipped logo');
          goToTemplates();
        }} style={{ flex: 1 }}>Skip for now</Button>
        <Button type="button" $size="sm" onClick={() => {
          const hasLogo = logoFile || logoUrlInput.trim() || logoPreview;
          if (hasLogo) {
            if (logoUrlInput.trim()) setSelectedLogo(logoUrlInput.trim());
            user('Logo added ✓');
          } else {
            user('Skipped logo');
          }
          goToTemplates();
        }} style={{ flex: 1 }} disabled={false}>
          {(logoFile || logoUrlInput.trim()) ? 'Continue' : 'Skip'}
        </Button>
      </div>
    </>
  );
}
